const { validator, MIN_VALID_DATE, MAX_VALID_DATE } = require('./validator');
const inOutFormatDecorator = require('./validator').inOutFormatDecorator;
const { formatYearMonth } = require('../lib/utils');

const commonProperties = {
    sort: {
        presence: false,
        numericality: {
            onlyInteger: true,
            greaterThanOrEqualTo: 0,
            lessThanOrEqualTo: 100
        }
    },
    itemName: {
        presence: {
            allowEmpty: false
        },
        length: {
            maximum: 60
        }
    }
};

const education = {
    id: { type: 'ID' },
    state: {
        presence: false,
        length: {
            maximum: 60
        }
    },
    country: {
        presence: false,
        length: {
            maximum: 60
        }
    },
    city: {
        presence: false,
        length: {
            maximum: 60
        }
    },
    status: {
        presence: false,
        length: {
            maximum: 60
        }
    },
    school: {
        presence: false,
        length: {
            maximum: 60
        }
    },
    qualification: {
        presence: false,
        length: {
            maximum: 60
        }
    },
    endDate: {
        presence: false,
        datetime: {
            earliest: MIN_VALID_DATE,
            latest: MAX_VALID_DATE,
            tooEarly: `must be after ${formatYearMonth(MIN_VALID_DATE)}`,
            tooLate: `must be before ${formatYearMonth(MAX_VALID_DATE)}`
        }
    },
    sort: commonProperties.sort,
    description: { type: 'string' },
    month: {
        presence: false,
        length: {
            maximum: 70
        }
    },
    year: {
        presence: false
    }
};

const employment = {
    id: { type: 'ID' },
    company: { type: 'string' },
    title: {
        length: {
            maximum: 60
        }
    },
    startDate: {
        presence: false,
        datetime: {
            earliest: MIN_VALID_DATE,
            latest: MAX_VALID_DATE
        }
    },
    endDate: {
        presence: false,
        isAfter: {
            startDate: 'startDate',
            errorMessage: 'should not be before start date.',
            condition: 'presentlyEmployed'
        },
        datetime: {
            earliest: MIN_VALID_DATE,
            latest: MAX_VALID_DATE
        }
    },
    city: {
        type: 'string',
        length: {
            maximum: 60
        },
    },
    state: {
        type: 'string',
        length: {
            maximum: 60
        },
    },
    country: {
        type: 'string',
        length: {
            maximum: 60
        },
    },
    description: { type: 'string' },
    presentlyEmployed: { type: 'boolean' },
    compensationInterval: { type: 'string' },
    compensationCurrency: { type: 'string' },
    compensationAmount: { type: 'string' },
    sort: commonProperties.sort
};

const createResume = {
    id: { type: 'ID' },
    userId: { type: 'stringID' },
    template: {
        type: 'string',
        presence: {
            allowEmpty: false
        }
    },
    title: {
        length: {
            maximum: 60
        }
    }
};

const duplicateResume = {
    id: { type: 'ID' },
    userId: { type: 'stringID' },
    title: {
        length: {
            maximum: 60
        }
    }
};

const skills = {
    id: { type: 'ID' },
    name: commonProperties.itemName,
    sort: commonProperties.sort,
    proficiency: { type: 'string' }
};

const awards = {
    id: { type: 'ID' },
    name: commonProperties.itemName,
    sort: commonProperties.sort
};

const certificates = {
    id: { type: 'ID' },
    name: commonProperties.itemName,
    sort: commonProperties.sort
};

const languages = {
    id: { type: 'ID' },
    name: commonProperties.itemName,
    sort: commonProperties.sort,
    proficiency: { type: 'string' }
};

const links = {
    id: { type: 'ID' },
    url: {
        type: 'string',
        presence: { allowEmpty: false }
    },
    sort: commonProperties.sort
};

const patents = {
    id: { type: 'ID' },
    sort: commonProperties.sort,
    title: {
        type: 'string',
        presence: { allowEmpty: false }
    },
    patentNumber: {
        type: 'string',
        presence: { allowEmpty: false }
    },
    url: { type: 'string' },
    month: {
        presence: false,
        length: {
            maximum: 70
        }
    },
    year: {
        presence: false
    },
    description: { type: 'string' },
};

const publications = {
    id: { type: 'ID' },
    sort: commonProperties.sort,
    title: {
        type: 'string',
        presence: { allowEmpty: false }
    },
    url: { type: 'string' },
    month: {
        presence: false,
        length: {
            maximum: 70
        }
    },
    year: {
        presence: false
    },
    description: { type: 'string' },
};

const groups = {
    id: { type: 'ID' },
    sort: commonProperties.sort,
    title: {
        type: 'string',
        presence: { allowEmpty: false }
    },
    presentlyIn: { type: 'boolean' },
    startDate: {
        presence: false,
        datetime: {
            earliest: MIN_VALID_DATE,
            latest: MAX_VALID_DATE
        }
    },
    endDate: {
        presence: false,
        isAfter: {
            startDate: 'startDate',
            errorMessage: 'should not be before start date.',
            condition: 'presentlyIn'
        },
        datetime: {
            earliest: MIN_VALID_DATE,
            latest: MAX_VALID_DATE
        }
    },
    description: { type: 'string' },
};

const militaryService = {
    id: { type: 'ID' },
    sort: commonProperties.sort,
    countryOfService: {
        type: 'string',
        presence: { allowEmpty: false }
    },
    branch: {
        type: 'string',
        presence: { allowEmpty: false }
    },
    rank: {
        type: 'string',
        presence: { allowEmpty: false }
    },
    presentlyIn: { type: 'boolean' },
    startDate: {
        presence: false,
        datetime: {
            earliest: MIN_VALID_DATE,
            latest: MAX_VALID_DATE
        }
    },
    endDate: {
        presence: false,
        isAfter: {
            startDate: 'startDate',
            errorMessage: 'should not be before start date.',
            condition: 'presentlyIn'
        },
        datetime: {
            earliest: MIN_VALID_DATE,
            latest: MAX_VALID_DATE
        }
    },
    description: { type: 'string' },
    commendations: { type: 'string' },
};

const contactDetails = {
    email: { type: 'string' },
    phone: { type: 'string' },
    firstName: { type: 'string' },
    lastName: { type: 'string' },
    address: { type: 'string' },
    city: { type: 'string' },
    state: { type: 'string' },
    zip: { type: 'string' },
    country: { type: 'string' },
    summary: { type: 'string' },
    jobCategory: { type: 'string' }
};

const updateSectionsName = {
    resumeId: { type: 'ID' },
    userId: { type: 'string' },
    sectionId: {
        type: 'string',
        presence: { allowEmpty: false }
    },
    newName: {
        type: 'string',
        presence: { allowEmpty: false }
    }
};

const section = {
    resumeId: { type: 'ID' },
    userId: { type: 'string' },
    description: { type: 'string' }
};

const patentSection = {
    ...section
};

const militaryServiceSection = {
    ...section
};

const groupsSection = {
    ...section
};
const publicationSection = {
    ...section
};

const linkSection = {
    ...section
};

const summarySection = {
    ...section
};

const certificateSection = {
    ...section
};

const skillSection = {
    ...section
};

const languageSection = {
    ...section
};

const awardSection = {
    ...section
};

const customSections = {
    id: { type: 'ID' },
    resumeId: { type: 'ID' },
    userId: { type: 'string' },
    name: { type: 'string' },
    description: { type: 'string' }
};

module.exports = {
    skills: {
        schema: skills,
        validate: inOutFormatDecorator(validator, skills)
    },
    certificates: {
        schema: certificates,
        validate: inOutFormatDecorator(validator, certificates)
    },
    awards: {
        schema: awards,
        validate: inOutFormatDecorator(validator, awards)
    },
    languages: {
        schema: languages,
        validate: inOutFormatDecorator(validator, languages)
    },
    createResume: {
        schema: createResume,
        validate: inOutFormatDecorator(validator, createResume)
    },
    duplicateResume: {
        schema: duplicateResume,
        validate: inOutFormatDecorator(validator, duplicateResume)
    },
    contactDetails: {
        schema: contactDetails,
        validate: inOutFormatDecorator(validator, contactDetails)
    },
    education: {
        schema: education,
        validate: inOutFormatDecorator(validator, education)
    },
    employment: {
        schema: employment,
        validate: inOutFormatDecorator(validator, employment)
    },
    updateSectionsName: {
        schema: updateSectionsName,
        validate: inOutFormatDecorator(validator, updateSectionsName)
    },
    summarySection: {
        schema: summarySection,
        validate: inOutFormatDecorator(validator, summarySection)
    },
    certificateSection: {
        schema: certificateSection,
        validate: inOutFormatDecorator(validator, certificateSection)
    },
    skillSection: {
        schema: skillSection,
        validate: inOutFormatDecorator(validator, skillSection)
    },
    languageSection: {
        schema: languageSection,
        validate: inOutFormatDecorator(validator, languageSection)
    },
    awardSection: {
        schema: awardSection,
        validate: inOutFormatDecorator(validator, awardSection)
    },
    customSection: {
        schema: customSections,
        validate: inOutFormatDecorator(validator, customSections)
    },
    links: {
        schema: links,
        validate: inOutFormatDecorator(validator, links)
    },
    linkSection: {
        schema: linkSection,
        validate: inOutFormatDecorator(validator, linkSection)
    },
    publications: {
        schema: publications,
        validate: inOutFormatDecorator(validator, publications)
    },
    publicationSection: {
        schema: publicationSection,
        validate: inOutFormatDecorator(validator, publicationSection)
    },
    groups: {
        schema: groups,
        validate: inOutFormatDecorator(validator, groups)
    },
    groupsSection: {
        schema: groupsSection,
        validate: inOutFormatDecorator(validator, groupsSection)
    },
    militaryService: {
        schema: militaryService,
        validate: inOutFormatDecorator(validator, militaryService)
    },
    militaryServiceSection: {
        schema: militaryServiceSection,
        validate: inOutFormatDecorator(validator, militaryServiceSection)
    },
    patents: {
        schema: patents,
        validate: inOutFormatDecorator(validator, patents)
    },
    patentSection: {
        schema: patentSection,
        validate: inOutFormatDecorator(validator, patentSection)
    },
};
